import { useMutation, UseMutationResult } from "@tanstack/react-query";
import {
  LOGIN_EMAIL_PASSWORD_KEY,
  loginWithEmailAndPassword,
  SIGN_UP_WITH_EMAIL_PASSWORD,
  signupWithEmailAndPassword,
} from "../components/auth/api/authApi";
import { useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ServerResponse } from "../types";
import { FunnelOnSuccess } from "../components/funnel/types";
import { useRefetchAccountAndRelease } from "./useRefetchAccountAndRelease";

export enum AuthType {
  login = "login",
  signup = "signup",
}
interface AuthMutationOptions {
  invalidate?: boolean;
  onSuccess?: FunnelOnSuccess;
  onError?: (error: unknown) => void;
}

/**
 * Custom hook for login mutations that automatically reports auth success to funnel state.
 * This eliminates the need to pass reportAuthSuccess through component props.
 */
export function useLoginMutation(
  options: AuthMutationOptions = {},
): UseMutationResult<any, unknown, { email: string; password: string }> {
  const { invalidate = true, onSuccess, onError } = options;
  const toast = useToast();
  const { t } = useTranslation();

  const refetchQueries = useRefetchAccountAndRelease();

  return useMutation({
    mutationKey: LOGIN_EMAIL_PASSWORD_KEY,
    mutationFn: ({ email, password }) =>
      loginWithEmailAndPassword(email, password),
    onSuccess: async () => {
      // Invalidate queries if needed
      if (invalidate) {
        const refreshResults = await refetchQueries();
        // Always call onSuccess with the refetch results when invalidating
        onSuccess?.({
          options: { authMethod: AuthType.login },
          data: refreshResults,
        });
      } else {
        // Call onSuccess without data when not invalidating
        onSuccess?.({ options: { authMethod: AuthType.login } });
      }
    },
    onError: (error) => {
      if (axios.isAxiosError<ServerResponse<unknown>>(error)) {
        const errorData = error.response?.data;
        const fieldsWithErrors = errorData?.payload.form_fields?.filter(
          (field) => field.error !== null,
        );

        if (fieldsWithErrors?.length) {
          toast({
            title: fieldsWithErrors[0]?.error,
            status: "error",
          });
        } else {
          toast({
            title: t("components.auth.loginFailed"),
            status: "error",
          });
        }
      }

      // Call custom error handler if provided
      onError?.(error);
    },
  });
}

/**
 * Custom hook for signup mutations that automatically reports auth success to funnel state.
 * This eliminates the need to pass reportAuthSuccess through component props.
 */
export function useSignupMutation(
  options: AuthMutationOptions = {},
): UseMutationResult<any, unknown, { email: string; password: string }> {
  const { invalidate = true, onSuccess, onError } = options;
  const toast = useToast();
  const { t } = useTranslation();

  const refetchQueries = useRefetchAccountAndRelease();

  return useMutation({
    mutationKey: SIGN_UP_WITH_EMAIL_PASSWORD,
    mutationFn: ({ email, password }) =>
      signupWithEmailAndPassword(email, password),
    onSuccess: async () => {
      // Invalidate queries if needed
      if (invalidate) {
        const refreshResults = await refetchQueries();
        // Always call onSuccess with the refetch results when invalidating
        onSuccess?.({
          options: { authMethod: AuthType.signup },
          data: refreshResults,
        });
      } else {
        // Call onSuccess without data when not invalidating
        onSuccess?.({ options: { authMethod: AuthType.signup } });
      }
    },
    onError: (error) => {
      if (axios.isAxiosError<ServerResponse<unknown>>(error)) {
        const errorData = error.response?.data;
        const fieldsWithErrors = errorData?.payload.form_fields?.filter(
          (field) => field.error !== null,
        );

        if (fieldsWithErrors?.length) {
          toast({
            title: fieldsWithErrors[0]?.error,
            status: "error",
          });
        } else {
          toast({
            title: t("components.auth.signUpFailed"),
            status: "error",
          });
        }
      }

      // Call custom error handler if provided
      onError?.(error);
    },
  });
}

/**
 * Custom hook for SSO authentication that determines auth method from current context.
 * Automatically reports auth success to funnel state without needing currentFunnelType prop.
 */
export function useSsoAuth(options: AuthMutationOptions = {}) {
  const { invalidate = true, onSuccess } = options;

  const refetchQueries = useRefetchAccountAndRelease();

  const reportSsoAuth = async (authMethod: AuthType) => {
    if (invalidate) {
      const refreshResults = await refetchQueries();
      // Always call onSuccess with the refetch results when invalidating
      onSuccess?.({
        options: { authMethod },
        data: refreshResults,
      });
    } else {
      onSuccess?.({ options: { authMethod } });
    }
  };

  return { reportSsoAuth };
}
